import React, { Component, ChangeEvent } from "react";
import {
    Modal, ModalBody, ModalHeader, ModalFooter
} from "reactstrap";
import * as KSLPlusAPI from "../services/KSLPlusAPI";
import { env } from '../env';
import '../custom.css';

type Props = {
    open: boolean;
    kunde: number | undefined;
    gemeinde: number | undefined;
    jahr: number | undefined;
    plantyp: number;
    planstufe: number;
    uhconfig: number | undefined;
    bundesland: string;
    buchungsart: number;
    token: string;
    onClose: () => void;
    onNeedsLogin: () => void;
}

type State = {
    vorlagen: KSLPlusAPI.BerichtsvorlageModel[];
    selectedVorlage: string;
    selectedSystem: boolean;
    format: "pdf" | "quell";
    jrPlanstufen: KSLPlusAPI.Planstufen[];
    jrJahr: number;
    selectedJRKey: string;
    loading: boolean;
    generating: boolean;
    toastVisible: boolean;
    toastMessage: string;
    toastVariant: "info" | "success" | "error";
}

export class BerichtErstellen extends Component<Props, State> {

    state: State = {
        vorlagen: [],
        selectedVorlage: "",
        selectedSystem: false,
        format: "quell",
        jrPlanstufen: [],
        jrJahr: 0,
        selectedJRKey: "",
        loading: false,
        generating: false,
        toastVisible: false,
        toastMessage: "",
        toastVariant: "info"
    }

    toastTimer: any = null;

    componentDidUpdate(prevProps: Props) {
        if (this.props.open && !prevProps.open) {
            this.loadData();
        }
    }

    componentWillUnmount() {
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }
    }

    getModul = (): number => {
        if (this.props.plantyp === 3)
            return 1;
        if (this.props.plantyp === 4)
            return 2;
        return 0;
    }

    needsJRAuswahl = (): boolean => {
        return this.props.plantyp === 1 || this.props.plantyp === 2;
    }

    loadData = () => {
        this.setState({
            vorlagen: [],
            selectedVorlage: "",
            selectedSystem: false,
            format: "quell",
            jrPlanstufen: [],
            jrJahr: 0,
            selectedJRKey: "",
            loading: true
        });

        this.loadVorlagen();

        if (this.needsJRAuswahl()) {
            this.loadJRPlanstufen(((this.props.jahr ?? 0) - 1));
        }
    }

    loadVorlagen = () => {
        const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
        var client = new KSLPlusAPI.DigitalHousholdAdmin_Client(api);

        var modul = this.getModul();
        var loadFct: Promise<KSLPlusAPI.BerichtsvorlageModel[]>;
        if (modul === 0) {
            loadFct = client.getVBTemplates(this.props.kunde, this.props.gemeinde, this.props.jahr, false, this.props.bundesland, this.props.token);
        } else if (modul === 1) {
            loadFct = client.getLBTemplates(this.props.kunde, this.props.gemeinde, this.props.jahr, false, this.props.bundesland, this.props.token);
        } else {
            loadFct = client.getUHBTemplates(this.props.kunde, this.props.gemeinde, this.props.jahr, false, this.props.bundesland, this.props.token);
        }

        loadFct.then(vorlagen => {
            var selectedVorlage = vorlagen.length > 0 ? (vorlagen[0].filename ?? "") : "";
            var selectedSystem = vorlagen.length > 0 ? (vorlagen[0].system ?? false) : false;
            this.setState({
                vorlagen: vorlagen,
                selectedVorlage: selectedVorlage,
                selectedSystem: selectedSystem,
                loading: false
            });
        }).catch(Reason => {
            if (Reason && Reason.status === 401) {
                this.props.onNeedsLogin();
            }
            this.setState({ vorlagen: [], loading: false });
        });
    }

    loadJRPlanstufen = (jahr: number) => {
        const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";

        var client = new KSLPlusAPI.DigitalHousholdAdmin_Client(api);
        var request = new KSLPlusAPI.JahrModel();
        request.kunde = this.props.kunde;
        request.gemeinde = this.props.gemeinde;
        request.jahr = jahr;

        client.planstufen(this.props.token, request).then(ps => {
            var jrPs = (ps ?? []).filter(p => p.plantyp === 3);
            if (jrPs.length === 0 && jahr === ((this.props.jahr ?? 0) - 1)) {
                this.loadJRPlanstufen(((this.props.jahr ?? 0) - 2));
                return;
            }
            var firstKey = jrPs.length > 0 ? this.buildJRKey(jrPs[0]) : "";
            this.setState({
                jrPlanstufen: jrPs,
                jrJahr: jahr,
                selectedJRKey: firstKey
            });
        }).catch(Reason => {
            if (jahr === ((this.props.jahr ?? 0) - 1)) {
                this.loadJRPlanstufen(((this.props.jahr ?? 0) - 2));
            } else {
                this.setState({ jrPlanstufen: [], jrJahr: jahr, selectedJRKey: "" });
            }
        });
    }

    buildJRKey = (p: KSLPlusAPI.Planstufen): string => {
        return (p.plantyp ?? 0) + "|" + (p.planstufe ?? 0);
    }

    handleVorlageChange = (event: ChangeEvent<HTMLSelectElement>) => {
        var filename = event.target.value;
        var vorlage = this.state.vorlagen.find(v => v.filename === filename);
        this.setState({
            selectedVorlage: filename,
            selectedSystem: vorlage?.system ?? false
        });
    }

    handleFormatChange = (event: ChangeEvent<HTMLInputElement>) => {
        this.setState({ format: event.target.value as "pdf" | "quell" });
    }

    handleJRChange = (event: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ selectedJRKey: event.target.value });
    }

    showToast = (message: string, variant: "info" | "success" | "error", autoHideMs?: number) => {
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
            this.toastTimer = null;
        }
        this.setState({ toastVisible: true, toastMessage: message, toastVariant: variant });
        if (autoHideMs && autoHideMs > 0) {
            this.toastTimer = setTimeout(() => {
                this.setState({ toastVisible: false });
                this.toastTimer = null;
            }, autoHideMs);
        }
    }

    buildUrl = (): string => {
        const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
        var controller = this.props.buchungsart === 2 ? "DigitalHousholdDoppikAdmin" : "DigitalHousholdAdmin";
        var modul = this.getModul();

        var berichtFilename = this.state.selectedVorlage;
        if (this.state.format === "pdf") {
            berichtFilename = berichtFilename + ".pdf";
        }

        var system = this.state.selectedSystem;
        var kunde = this.props.kunde ?? 0;
        var gemeinde = this.props.gemeinde ?? 0;
        var jahr = this.props.jahr ?? 0;
        var plantyp = this.props.plantyp;
        var planstufe = this.props.planstufe;

        if (modul === 0) {
            var jrPlantyp = 3;
            var jrPlanstufe = 0;
            if (this.state.selectedJRKey) {
                var parts = this.state.selectedJRKey.split("|");
                jrPlantyp = +parts[0];
                jrPlanstufe = +parts[1];
            }
            return api + "/api/" + controller + "/GetVBFile/"
                + kunde + "/"
                + gemeinde + "/"
                + jahr + "/"
                + plantyp + "/"
                + planstufe + "/"
                + encodeURIComponent(berichtFilename) + "/"
                + "false/"
                + plantyp + "/"
                + planstufe + "/"
                + jrPlanstufe + "/"
                + this.state.jrJahr + "/"
                + system + "/"
                + (this.props.bundesland ?? "");
        } else if (modul === 1) {
            return api + "/api/" + controller + "/GetLBFile/"
                + kunde + "/"
                + gemeinde + "/"
                + jahr + "/"
                + plantyp + "/"
                + planstufe + "/"
                + encodeURIComponent(berichtFilename) + "/"
                + "false/"
                + system + "/"
                + (this.props.bundesland ?? "");
        } else {
            return api + "/api/" + controller + "/GetUHBFile/"
                + kunde + "/"
                + gemeinde + "/"
                + jahr + "/"
                + plantyp + "/"
                + planstufe + "/"
                + (this.props.uhconfig ?? 0) + "/"
                + encodeURIComponent(berichtFilename) + "/"
                + "false/"
                + system + "/"
                + (this.props.bundesland ?? "");
        }
    }

    handleGenerate = () => {
        if (!this.state.selectedVorlage) {
            this.showToast("Bitte eine Vorlage auswählen", "error", 5000);
            return;
        }
        if (this.needsJRAuswahl() && this.state.jrPlanstufen.length > 0 && !this.state.selectedJRKey) {
            this.showToast("Bitte eine JR-Planstufe auswählen", "error", 5000);
            return;
        }

        var token = this.props.token;
        if (!token) {
            this.props.onNeedsLogin();
            return;
        }

        var url = this.buildUrl();
        var downloadName = (this.state.selectedVorlage || "Bericht") + (this.state.format != "quell" ? ("." + this.state.format) : "");

        this.setState({ generating: true });
        this.props.onClose();
        this.showToast("Bericht wird erstellt...", "info");

        fetch(url, {
            headers: { "Authorization": token }
        }).then(r => {
            if (r.status === 401) {
                this.props.onNeedsLogin();
                throw new Error("Unauthorized");
            }
            if (!r.ok) {
                throw new Error("Status " + r.status);
            }
            return r.blob();
        }).then(b => {
            if (b) {
                var a = document.createElement("a");
                a.href = URL.createObjectURL(b);
                a.download = downloadName;
                a.click();
                URL.revokeObjectURL(a.href);
            }
            this.setState({ generating: false });
            this.showToast("Bericht erfolgreich erstellt", "success", 3000);
        }).catch(() => {
            this.setState({ generating: false });
            this.showToast("Fehler bei der Berichterstellung", "error", 5000);
        });
    }

    renderToast = () => {
        if (!this.state.toastVisible)
            return null;

        var toastClass = "be-toast be-toast-info";
        if (this.state.toastVariant === "success")
            toastClass = "be-toast be-toast-success";
        else if (this.state.toastVariant === "error")
            toastClass = "be-toast be-toast-error";

        return (
            <div className="be-toast-container">
                <div className={toastClass}>
                    {this.state.generating ?
                        <div className="be-toast-spinner" />
                        :
                        <span className="be-toast-icon">
                            {this.state.toastVariant === "success" ? "\u2713" : this.state.toastVariant === "error" ? "\u2715" : "\u2139"}
                        </span>
                    }
                    <span>{this.state.toastMessage}</span>
                </div>
            </div>
        );
    }

    render() {
        var jrDisplay = (p: KSLPlusAPI.Planstufen): string => {
            var datum = p.datum ? new Date(p.datum).toLocaleDateString("de-DE") : "";
            return "Plantyp " + (p.plantyp ?? 0) + " / Stufe " + (p.planstufe ?? 0) + (datum ? " (" + datum + ")" : "");
        };

        return (
            <>
                <Modal className="be-modal" isOpen={this.props.open} toggle={() => this.props.onClose()} backdrop="static">
                    <ModalHeader toggle={() => this.props.onClose()}>Bericht erstellen</ModalHeader>
                    <ModalBody>
                        {this.state.loading ?
                            <div className="be-loading">
                                <div className="be-toast-spinner" />
                                <span>Vorlagen werden geladen...</span>
                            </div>
                            :
                            <>
                                <div className="be-form-group">
                                    <label className="be-label">Berichtsvorlage</label>
                                    {this.state.vorlagen.length === 0 ?
                                        <div className="be-empty-state">Keine Vorlagen verfügbar</div>
                                        :
                                        <select className="be-select" value={this.state.selectedVorlage} onChange={this.handleVorlageChange}>
                                            {this.state.vorlagen.map((v, index) => (
                                                <option key={index} value={v.filename ?? ""}>{(v.filename ?? "") + (v.system ? " (System)" : "")}</option>
                                            ))}
                                        </select>
                                    }
                                </div>
                                <div className="be-form-group" >
                                    <label className="be-label">Ausgabeformat</label>
                                    <div className="be-radio-group">
                                        <label className={"be-radio-label" + (this.state.format === "quell" ? " be-radio-checked" : "")}>
                                            <input type="radio" name="berichtformat" value="quell" checked={this.state.format === "quell"} onChange={this.handleFormatChange} />
                                            Vorlageformat
                                        </label>
                                        <label className={"be-radio-label" + (this.state.format === "pdf" ? " be-radio-checked" : "")}>
                                            <input type="radio" name="berichtformat" value="pdf" checked={this.state.format === "pdf"} onChange={this.handleFormatChange} />
                                            PDF
                                        </label>
                                    </div>
                                </div>
                                {this.needsJRAuswahl() &&
                                    <div className="be-form-group">
                                        <label className="be-label">JR-Planstufe (Vorjahr)</label>
                                        {this.state.jrPlanstufen.length === 0 ?
                                            <div className="be-empty-state">Keine JR-Planstufen im Vorjahr/Vorvorjahr vorhanden</div>
                                            :
                                            <select className="be-select" value={this.state.selectedJRKey} onChange={this.handleJRChange}>
                                                {this.state.jrPlanstufen.map((p, index) => (
                                                    <option key={index} value={this.buildJRKey(p)}>
                                                        {this.state.jrJahr + ": " + jrDisplay(p)}
                                                    </option>
                                                ))}
                                            </select>
                                        }
                                    </div>
                                }
                            </>
                        }
                    </ModalBody>
                    <ModalFooter>
                        <button type="button" className="be-btn be-btn-secondary" onClick={() => this.props.onClose()}>Abbrechen</button>
                        <button type="button" className="be-btn be-btn-primary" onClick={this.handleGenerate} disabled={this.state.loading || !this.state.selectedVorlage}>Bericht erstellen</button>
                    </ModalFooter>
                </Modal>
                {this.renderToast()}
            </>
        );
    }
}
